import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useDeleteProviderMutation,
  useGetProvidersByAccountQuery,
} from "store/queries/providers";
import styled from "styled-components";
import { IProvider } from "types/Provider/Provider";

import { ProvidersTable } from "./components/ProvidersTable";

const Container = styled.div`
  display: flex;
  align-items: end;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
`;

export function Providers() {
  const navigate = useNavigate();
  const { id: accountId } = useParams();
  const { setIsLoading } = useLoading();
  const [first, setFirst] = useState(0);
  const [rows] = useState(25);
  useDocumentTitle("Provider List");

  const [selectedProviders, setSelectedProviders] = useState<IProvider[]>([]);

  const [deleteMutation] = useDeleteProviderMutation();
  const { isLoading, data: providers } = useGetProvidersByAccountQuery({
    accountId,
    page: first,
    pageSize: rows,
  });
  const { current: toastElement } = useContext(ToastContext);

  // CLE-2799 — the API refuses any provider that is actually in use (payments, estimates,
  // claims, contracts, payer rules, ...) and says why. Each provider is reported on its own:
  // this used to show "Deleted!" even when every delete had failed.
  const deleteSelectedProviders = async () => {
    setIsLoading(true);
    const deletedNames: string[] = [];
    const refusals: string[] = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < selectedProviders.length; i++) {
      const aProvider = selectedProviders[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        await deleteMutation(aProvider.id).unwrap();
        deletedNames.push(aProvider.name);
      } catch (error: any) {
        refusals.push(
          error?.data?.message ?? `${aProvider.name} could not be deleted.`
        );
      }
    }

    setSelectedProviders([]);
    setIsLoading(false);

    if (deletedNames.length) {
      toastElement?.show({
        severity: "success",
        summary: "Deleted",
        detail: `Deleted ${deletedNames.join(", ")}.`,
      });
    }
    refusals.forEach((detail) =>
      toastElement?.show({
        severity: "error",
        summary: "Not deleted",
        detail,
        sticky: true,
      })
    );
  };

  const handleOnClickDeleteProviders = () => {
    const count = selectedProviders.length;
    confirmDialog({
      header: count === 1 ? "Delete provider?" : `Delete ${count} providers?`,
      icon: "pi pi-exclamation-triangle",
      message: (
        <div>
          <p className="mb-2">
            <strong>
              {selectedProviders.map((provider) => provider.name).join(", ")}
            </strong>{" "}
            will be removed permanently, with its fee schedule.
          </p>
          <p className="mb-0 text-600">
            Only a provider added by mistake can be deleted. One with payments,
            estimates, claims, reports, contracts or payer rules will be
            refused.
          </p>
        </div>
      ),
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: deleteSelectedProviders,
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Container>
        <StyledButton
          icon="pi pi-plus"
          className="p-button-raised"
          onClick={() => navigate(`/providers/new?accountId=${accountId}`)}
        >
          Add new provider
        </StyledButton>
      </Container>
      <ProvidersTable
        {...{ selectedProviders }}
        providers={providers}
        onSelectChange={(event) => setSelectedProviders(event.value)}
        first={first}
        setFirst={setFirst}
        rows={rows}
      />
      <Container>
        <Button
          label="Delete"
          className="p-button-outlined mt-4"
          disabled={!selectedProviders.length}
          onClick={handleOnClickDeleteProviders}
        />
      </Container>
    </>
  );
}
